import { Component, EventEmitter, Inject, Input, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { AccountGroup, ProjectionFilter } from '@vendasta/account-group';
import { BusinessSelectorDialogComponent, DialogData } from '@vendasta/businesses';
import { Sender, SenderType } from '@vendasta/email';
import { BehaviorSubject, Observable, firstValueFrom, map } from 'rxjs';
import { switchMap, take } from 'rxjs/operators';
import { EmailPreviewHydrationData } from '../../services/preview-rendering.service';
import { SENDER_INJECTION_TOKEN } from '../../shared';
import { ContactData, ContactSelectorComponent } from './contact-selector-modal/contact-selector.component';

export interface SelectedPreviewContext {
  accountGroupId: string;
  name: string;
  hasSalesperson: boolean;
  hasSnapshot: boolean;
  contactID?: string;
  useFakeData?: boolean;
}

export type Layout = 'vertical' | 'horizontal';

@Component({
  selector: 'email-preview-selector',
  templateUrl: './preview-context-selector.component.html',
  styleUrls: ['./preview-context-selector.component.scss'],
  standalone: false,
})
export class PreviewContextSelectorComponent {
  name: string;
  showWarning$: Observable<boolean>;
  warningKey$?: string;
  dialogData?: DialogData;

  public selectedName: BehaviorSubject<string> = new BehaviorSubject<string>('');

  @Input() set hydrationData(v: EmailPreviewHydrationData) {
    const biz = v?.business;
    this.name = biz?.name || 'None selected';
    this.showWarning$ = this.shouldShowWarning(biz);
    this.warningKey$ = biz ? this.getWarningKey(biz) : '';
    this.dialogData = {
      partnerId: v?.partnerID,
      marketId: v?.marketID,
      projectionFilterOverride: {
        napData: true,
        accountGroupExternalIdentifiers: true,
        snapshotReports: true,
      } as ProjectionFilter,
    };
  }

  @Input() layout: Layout = 'horizontal';

  @Output() contactSelected = new EventEmitter<string>();

  @Output() contextSelected = new EventEmitter<SelectedPreviewContext>();

  private shouldShowWarning(biz: { hasSnapshot: boolean; hasSalesperson: boolean }): Observable<boolean> {
    return this.sender$.pipe(
      map((sender) => {
        if (sender.type === SenderType.SENDER_TYPE_PARTNER && biz) {
          return !biz.hasSnapshot || !biz.hasSalesperson;
        } else {
          return false;
        }
      }),
    );
  }

  private getWarningKey(biz: {
    accountGroupId: string;
    name: string;
    hasSnapshot: boolean;
    hasSalesperson: boolean;
  }): string {
    if (!biz.hasSalesperson && !biz.hasSnapshot) {
      return 'EMAIL_TEMPLATES.PREVIEW.BUSINESS_SELECTOR.NO_SNAPSHOT_OR_SALESPERSON_WARNING';
    }
    if (!biz.hasSnapshot) {
      return 'EMAIL_TEMPLATES.PREVIEW.BUSINESS_SELECTOR.NO_SNAPSHOT_WARNING';
    }
    if (!biz.hasSalesperson) {
      return 'EMAIL_TEMPLATES.PREVIEW.BUSINESS_SELECTOR.NO_SALESPERSON_WARNING';
    }
    return '';
  }

  public contextName$: Observable<string>;

  constructor(
    private readonly translate: TranslateService,
    private readonly dialog: MatDialog,
    @Inject(SENDER_INJECTION_TOKEN) private readonly sender$: Observable<Sender>,
  ) {
    this.contextName$ = this.sender$.pipe(
      switchMap((sender) => {
        if (sender.type === SenderType.SENDER_TYPE_PARTNER) {
          return this.translate.stream('EMAIL_BUILDER.CONTEXT_SELECTOR.BUSINESS');
        } else {
          return this.translate.stream('EMAIL_BUILDER.CONTEXT_SELECTOR.CONTACT');
        }
      }),
    );
  }

  async openSelectDialog(): Promise<void> {
    return await firstValueFrom(
      this.sender$.pipe(
        take(1),
        map((sender) => {
          if (sender.type === SenderType.SENDER_TYPE_PARTNER) {
            return this.openBusinessSelectDialog();
          } else {
            return this.openContactSelectDialog();
          }
        }),
      ),
    );
  }

  async openContactSelectDialog(): Promise<void> {
    const dialog = this.dialog.open(ContactSelectorComponent, {
      data: this.sender$.pipe(map((sender) => ({ accountGroupID: sender.id }))),
      minWidth: '350px',
    });
    const contact: ContactData = await firstValueFrom(dialog.afterClosed());
    if (contact) {
      const sel: SelectedPreviewContext = {
        accountGroupId: contact.accountGroupID,
        name: contact.name,
        hasSnapshot: false,
        hasSalesperson: false,
        contactID: contact.contactID,
        useFakeData: false,
      };
      this.selectedName.next(contact.name);
      this.contactSelected.emit(contact.contactID);
      this.name = sel.name;
      this.showWarning$ = this.shouldShowWarning(sel);
      this.contextSelected.emit(sel);
    }
  }

  async openBusinessSelectDialog(): Promise<void> {
    const dialog = this.dialog.open(BusinessSelectorDialogComponent, {
      data: this.dialogData,
    });
    const ag: AccountGroup = await firstValueFrom(dialog.afterClosed());
    if (ag) {
      const sel: SelectedPreviewContext = {
        accountGroupId: ag.accountGroupId,
        name: ag.napData.companyName,
        hasSnapshot: ag.snapshotReports?.snapshots?.length > 0,
        hasSalesperson: !!ag.accountGroupExternalIdentifiers?.salesPersonId,
      };
      this.selectedName.next(sel.name);

      this.name = sel.name;
      this.showWarning$ = this.shouldShowWarning(sel);
      this.contextSelected.emit(sel);
    }
  }
}
